import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ElementType, FC } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from 'primitives/Button';

import { UnitTransferNextAction } from 'utils/enums/unit-transfer-next-action';

// Custom card instead of the generic Alert primitive — this is the single most important line on
// the whole tracker ("what do I do now?"), so it gets its own illustration-style icon per tone
// rather than sharing the small inline warning triangle every other banner on the page uses.
const TONE_STYLES: Record<
  UnitTransferNextAction['tone'],
  { badgeBg: string; iconColor: string; accent: string; icon: ElementType }
> = {
  info: {
    badgeBg: 'bg-blue-50',
    iconColor: 'text-blue-500',
    accent: 'border-l-blue-400',
    icon: ClockIcon,
  },
  success: {
    badgeBg: 'bg-green-50',
    iconColor: 'text-green-500',
    accent: 'border-l-green-500',
    icon: CheckCircleIcon,
  },
  warning: {
    badgeBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    accent: 'border-l-amber-400',
    icon: ExclamationTriangleIcon,
  },
  error: {
    badgeBg: 'bg-red-50',
    iconColor: 'text-red-500',
    accent: 'border-l-red-400',
    icon: XCircleIcon,
  },
};

const NextActionCard: FC<{ nextAction: UnitTransferNextAction | null }> = ({ nextAction }) => {
  const navigate = useNavigate();

  if (!nextAction) return null;

  const { badgeBg, iconColor, accent, icon: Icon } = TONE_STYLES[nextAction.tone];

  return (
    <div
      className={`flex flex-col gap-4 rounded-lg border-l-4 bg-white p-5 shadow-sm ring-1 ring-gray-200 ${accent}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${badgeBg}`}
        >
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-gray-900">{nextAction.title}</p>
          <p className="mt-1 text-sm text-gray-600">{nextAction.body}</p>
          {nextAction.ctaLabel && nextAction.ctaPath ? (
            <div className="mt-4">
              <Button onClick={() => navigate(nextAction.ctaPath as string)}>
                {nextAction.ctaLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default NextActionCard;
